/**
 * TrackingManager - Central Orchestrator
 *
 * Singleton that manages all tracking plugins and provides a unified API.
 * Handles:
 * - Plugin registration and initialization
 * - Event deduplication
 * - Attribution capture
 * - Concurrent firing to multiple platforms
 * - Event queuing before initialization
 */

import { trackingLog as log } from "../logger";
import { AttributionCapture } from "./attribution";
import { getEventConfig } from "./registry";
import { hasTrackingConsent } from "../consent";
import type {
  EventName,
  QueuedEvent,
  TrackingContext,
  TrackingPlugin,
  TrackingResult,
  UnifiedEvent,
} from "./types";

// Funnel value calculation (existing logic)
function calculateFunnelValue(
  funnelAnswers?: Record<string, string>
): number {
  if (!funnelAnswers || Object.keys(funnelAnswers).length === 0) {
    return 0;
  }

  let yesCount = 0;
  let totalAnswers = 0;

  Object.values(funnelAnswers).forEach(answer => {
    if (typeof answer === "string") {
      totalAnswers++;
      const normalized = answer.trim().toLowerCase();
      if (normalized === "ja" || normalized === "yes" || normalized === "oui") {
        yesCount++;
      }
    }
  });

  if (totalAnswers === 0) return 0;

  const completionRatio = yesCount / totalAnswers;
  return Math.round(Math.pow(completionRatio, 4) * 100);
}

/**
 * Generate unique event ID for deduplication.
 * Contains no PII: it lands in the dataLayer, where every GTM tag can read it.
 */
export function generateEventId(event: EventName): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  return `${event}_${timestamp}_${random}`;
}

// Raw PII never goes into event params: the client plugins forward params to
// gtag (dataLayer, readable by every GTM tag) and fbq. Identity belongs in
// `context`, which is hashed before it leaves the browser.
const PII_PARAM_KEYS = new Set(["email", "name", "phone", "street", "zip", "city", "year"]);

export function stripPiiParams(
  params: Record<string, unknown> = {}
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => !PII_PARAM_KEYS.has(key))
  );
}

class TrackingManager {
  private static instance: TrackingManager;
  private plugins: Map<string, TrackingPlugin> = new Map();
  private initialized = false;
  private initializing = false;
  private eventQueue: QueuedEvent[] = [];
  private processedEventIds: Set<string> = new Set();

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): TrackingManager {
    if (!TrackingManager.instance) {
      TrackingManager.instance = new TrackingManager();
    }
    return TrackingManager.instance;
  }

  /**
   * Register a tracking plugin
   */
  registerPlugin(name: string, plugin: TrackingPlugin): void {
    this.plugins.set(name, plugin);
  }

  /**
   * Initialize all plugins
   */
  async initialize(): Promise<void> {
    if (this.initialized || this.initializing) return;
    this.initializing = true;

    try {
      // Initialize all plugins concurrently
      await Promise.all(
        Array.from(this.plugins.values()).map(plugin =>
          plugin.initialize().catch(err => {
            log.error(`Plugin ${plugin.name} failed to initialize`, err);
          })
        )
      );

      this.initialized = true;
      log.info("Initialized", { plugins: Array.from(this.plugins.keys()) });

      // Process queued events
      await this.processQueue();
    } finally {
      this.initializing = false;
    }
  }

  /**
   * Check if tracking should be skipped
   */
  private shouldSkip(): boolean {
    if (typeof window === "undefined") return true;

    // Skip on localhost/development/staging
    const hostname = window.location.hostname;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".vercel.app")
    )
      return true;

    // Skip if user declined cookies
    if (!hasTrackingConsent()) return true;

    // Skip if DNT enabled
    if (navigator.doNotTrack === "1") return true;

    // Skip internal/test users
    const isInternalUser =
      document.cookie.includes("internal_user=true") ||
      window.localStorage.getItem("internal_user") === "true";
    if (isInternalUser) return true;

    // Skip admin and partner users to avoid polluting analytics
    // Cookie is set by middleware on every request, so it's always fresh
    const roleCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("user_role="));
    if (roleCookie) {
      const role = roleCookie.split("=")[1];
      if (role === "admin" || role === "partner") return true;
    }

    return false;
  }

  /**
   * Main tracking method - single entry point for all events
   */
  async track(
    eventName: EventName,
    params?: Record<string, unknown>,
    context?: TrackingContext
  ): Promise<TrackingResult> {
    // Skip if tracking disabled
    if (this.shouldSkip()) {
      return { skipped: true, reason: "tracking_disabled" };
    }

    // Queue if not initialized
    if (!this.initialized) {
      this.eventQueue.push({
        eventName,
        params,
        context,
        timestamp: Date.now(),
      });
      log.debug(`Queued event: ${eventName} (not initialized)`);
      return { skipped: true, reason: "queued" };
    }

    // Generate event ID
    const eventId = generateEventId(eventName);

    // Dedupe check
    if (this.processedEventIds.has(eventId)) {
      return { skipped: true, reason: "duplicate" };
    }
    this.processedEventIds.add(eventId);

    // Get event configuration
    const config = getEventConfig(eventName);

    // Capture fresh attribution
    const attribution = await AttributionCapture.capture();

    // Calculate value if needed
    let calculatedParams = { ...config.defaults, ...stripPiiParams(params) };
    if (config.valueCalculation === "funnel" && params?.funnel_answers) {
      const value = calculateFunnelValue(
        params.funnel_answers as Record<string, string>
      );
      calculatedParams = { ...calculatedParams, value };
    }

    // Build unified event
    const event: UnifiedEvent = {
      eventId,
      eventName,
      timestamp: Date.now(),
      params: calculatedParams,
      attribution,
      context,
    };

    // Fire to all supporting plugins concurrently
    const results = await Promise.allSettled(
      Array.from(this.plugins.entries()).map(async ([name, plugin]) => {
        if (!plugin.supports(eventName)) {
          return {
            name,
            result: { skipped: true, reason: "event_not_supported" },
          };
        }
        if (!plugin.isReady()) {
          return { name, result: { skipped: true, reason: "plugin_not_ready" } };
        }
        const result = await plugin.track(event);
        return { name, result };
      })
    );

    // Process results
    const processedResults = results.map((r, i) => {
      if (r.status === "fulfilled") {
        return r.value;
      }
      const pluginName = Array.from(this.plugins.keys())[i];
      return {
        name: pluginName,
        result: {
          success: false,
          error: r.reason?.message || "unknown_error",
        },
      };
    });

    log.info(`Event: ${eventName}`, {
      eventId,
      results: processedResults.map(r => ({
        plugin: r.name,
        success: "success" in r.result ? r.result.success : false,
        skipped: "skipped" in r.result ? r.result.skipped : false,
      })),
    });

    return { eventId, results: processedResults };
  }

  /**
   * Process queued events after initialization
   */
  private async processQueue(): Promise<void> {
    const queue = [...this.eventQueue];
    this.eventQueue = [];

    for (const item of queue) {
      await this.track(item.eventName, item.params, item.context);
    }

    if (queue.length > 0) {
      log.info(`Processed ${queue.length} queued events`);
    }
  }

  /**
   * Clear processed event IDs (for testing)
   */
  clearDedupe(): void {
    this.processedEventIds.clear();
  }

  /**
   * Get plugin by name (for testing)
   */
  getPlugin(name: string): TrackingPlugin | undefined {
    return this.plugins.get(name);
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const trackingManager = TrackingManager.getInstance();
